
import React, { useState } from 'react';
import { Menu, X, Download, Trophy } from 'lucide-react';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalImages, setTotalImages] = useState(0);

  const IMAGES_PER_PAGE = 30;

  // Function to fetch images from your Cloudflare Worker API
  const fetchImages = async (page = 1, append = false) => {
    setLoading(true);
    try {
      const response = await fetch(`https://zt-prospects-7u-jc-gallery-api.13bytes.workers.dev/api/gallery?page=${page}&limit=${IMAGES_PER_PAGE}`);
      const data = await response.json();

      if (append) {
        setGalleryImages(prev => [...prev, ...data.images]);
      } else {
        setGalleryImages(data.images);
      }

      setTotalImages(data.pagination.total);
      setHasMore(data.pagination.hasMore);
      setGalleryPage(page);
    } catch (error) {
      console.error('Error fetching images:', error);
      // Fallback to sample data for demo
      const sampleImages = [
        {
          id: 1,
          url: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=800&h=600&fit=crop',
          filename: 'game_day_action.jpg',
          title: 'Game Day Action',
          date: '2024-06-10'
        },
        {
          id: 2,
          url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=600&fit=crop',
          filename: 'team_celebration.jpg',
          title: 'Team Celebration',
          date: '2024-06-08'
        },
        {
          id: 3,
          url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
          filename: 'practice_session.jpg',
          title: 'Practice Session',
          date: '2024-06-05'
        },
        {
          id: 4,
          url: 'https://images.unsplash.com/photo-1544737151444-6bdcbd7ca473?w=800&h=600&fit=crop',
          filename: 'championship_game.jpg',
          title: 'Championship Game',
          date: '2024-06-01'
        },
        {
          id: 5,
          url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
          filename: 'team_huddle.jpg',
          title: 'Team Huddle',
          date: '2024-05-30'
        },
        {
          id: 6,
          url: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=800&h=600&fit=crop',
          filename: 'awards_ceremony.jpg',
          title: 'Awards Ceremony',
          date: '2024-05-28'
        }
      ];
      setGalleryImages(sampleImages);
      setTotalImages(432);
      setHasMore(true);
    } finally {
      setLoading(false);
    }
  };

  // Load images when the gallery page is first accessed
  React.useEffect(() => {
    if (currentPage === 'gallery' && galleryImages.length === 0) {
      fetchImages(1);
    }
  }, [currentPage]);

  const Navigation = () => (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-3xl flex items-center justify-center">
              <img
                src="/logo_zt.png"
                alt="ZT Prospects Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-white font-bold text-xl">ZT PROSPECTS 7U JC</span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => setCurrentPage('home')}
              className={`text-blue-400 hover:text-pink-400 transition-colors ${currentPage === 'home' ? 'text-pink-400' : ''}`}
            >
              Home
            </button>
            <button
              onClick={() => setCurrentPage('gallery')}
              className={`text-blue-400 hover:text-pink-400 transition-colors ${currentPage === 'gallery' ? 'text-pink-400' : ''}`}
            >
              Gallery
            </button>
          </div>

          <button
            className="md:hidden text-blue-600 mr-4"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-blue-600">
            <div className="flex flex-col space-y-4 pt-4">
              <button
                onClick={() => { setCurrentPage('home'); setIsMenuOpen(false); }}
                className="text-white hover:text-blue-400 transition-colors text-left"
              >
                Home
              </button>
              <button
                onClick={() => { setCurrentPage('gallery'); setIsMenuOpen(false); }}
                className="text-white hover:text-blue-400 transition-colors text-left"
              >
                Gallery
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  const HomePage = () => (
    <div className="min-h-screen pb-10 bg-gradient-to-br from-blue-500 via-white to-pink-600">
      <div className="pt-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img
                    src="/usssa_iws.webp"
                    alt="USSSA IWS Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="inline-flex items-center space-x-2 bg-blue-600/20 px-4 py-2 rounded-full border border-blue-600/30">
                  <Trophy className="w-4 h-4 text-blue-500" />
                  <span className="text-pink-500 text-sm font-medium">2025 USSSA IWS</span>
                </div>

                <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                  Play Hard,<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                    Dream Big
                  </span>
                </h1>

                <p className="text-xl text-blue-500 leading-relaxed max-w-lg">
                  Win as a team, lose as a team. Our end of the season memories.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setCurrentPage('gallery')}
                  className="bg-pink-500 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                >
                  View Gallery
                </button>
              </div>
            </div>

            {/* Right Side - Team Photo */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-blue-500/20 rounded-3xl p-1 backdrop-blur-sm border border-white/10">
                <img
                  src="https://pub-34c965be2add4202a6048b96a343bfc3.r2.dev/team2.jpg"
                  alt="ZT Prospects 7U JC"
                  className="w-full h-full object-cover rounded-2xl"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-1">2024-2025 ZT Prospects 7U JC</h3>
                  <p className="text-gray-300 text-sm">USSSA International World Series</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const GalleryPage = () => {
    const totalPages = Math.ceil(totalImages / IMAGES_PER_PAGE);

    const handleLoadMore = () => {
      if (hasMore && !loading) {
        fetchImages(galleryPage + 1, true);
      }
    };

    const handlePageChange = (page) => {
      if (page !== galleryPage && !loading) {
        fetchImages(page, false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const downloadImage = async (imageUrl, filename) => {
      try {
        // Check if we're on a mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobile) {
          // For mobile devices, simply open the image in a new tab
          // This allows users to long-press and save the image
          window.open(imageUrl, '_blank', 'noopener,noreferrer');
        } else {
          // Desktop/laptop download approach (your existing code)
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = filename || 'baseball_team_photo.jpg';

          // For better browser compatibility
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback: always open in new tab
        window.open(imageUrl, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-white to-pink-600 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
              Team <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Gallery</span>
            </h1>
            <div className="mt-4">
              <span className="text-blue-500 font-medium">
                {totalImages} total photos • Page {galleryPage} of {totalPages}
              </span>
            </div>
          </div>

          {loading && galleryImages.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
              <span className="ml-3 text-white">Loading photos...</span>
            </div>
          ) : (
            <>
              {/* Gallery Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {galleryImages.map((image) => (
                  <div key={image.id} className="group relative bg-white/20 rounded-2xl overflow-hidden border border-white/30 hover:border-pink-600/50 transition-all duration-300 backdrop-blur-sm">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.title || image.filename}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-semibold mb-1 text-sm">
                          {image.title || image.filename}
                        </h3>
                        {image.date && (
                          <p className="text-blue-500 text-xs mb-3">{image.date}</p>
                        )}
                        <button
                          onClick={() => downloadImage(image.url, image.filename)}
                          className="flex items-center space-x-2 bg-pink-600 hover:bg-pink-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>

                    {/* Download button always visible on mobile */}
                    <div className="absolute top-2 right-2 md:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={() => downloadImage(image.url, image.filename)}
                        className="bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg backdrop-blur-sm transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="mt-12 flex flex-col items-center space-y-6">
                {/* Load More Button (for infinite scroll style) */}
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 border border-blue-500 backdrop-blur-sm"
                  >
                    {loading ? 'Loading...' : 'Load More Photos'}
                  </button>
                )}

                {/* Page Numbers */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(galleryPage - 1)}
                    disabled={galleryPage <= 1 || loading}
                    className="px-4 py-2 rounded-lg bg-white/40 text-pink-600 hover:bg-white/20 disabled:opacity-50 transition-colors"
                  >
                    Previous
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = galleryPage <= 3
                        ? i + 1
                        : galleryPage >= totalPages - 2
                          ? totalPages - 4 + i
                          : galleryPage - 2 + i;

                      if (pageNum > 0 && pageNum <= totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                              pageNum === galleryPage
                                ? 'bg-pink-600 text-white'
                                : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(galleryPage + 1)}
                    disabled={galleryPage >= totalPages || loading}
                    className="px-4 py-2 rounded-lg bg-white/20 text-pink-500 hover:bg-white/30 disabled:opacity-50 transition-colors"
                  >
                    Next
                  </button>
                </div>

                {/* Page Info */}
                <div className="text-blue-500 text-sm">
                  Showing {((galleryPage - 1) * IMAGES_PER_PAGE) + 1} - {Math.min(galleryPage * IMAGES_PER_PAGE, totalImages)} of {totalImages} photos
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      {currentPage === 'home' ? <HomePage /> : <GalleryPage />}
    </div>
  );
};

export default App;